import Vue from 'vue'
import VueRouter from 'vue-router'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import { getToken } from '@/utils/auth'
import { recordVisit } from '@/api/visit'

Vue.use(VueRouter)

const routes = [
  // 后台登录
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('@/views/admin/Login.vue'),
    meta: { title: '登录' }
  },
  // 后台管理
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    redirect: '/admin/dashboard',
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      { path: 'dashboard', name: 'Dashboard', component: () => import('@/views/admin/Dashboard.vue'), meta: { title: '仪表盘' } },
      { path: 'articles', name: 'ArticleManage', component: () => import('@/views/admin/ArticleManage.vue'), meta: { title: '文章管理' } },
      { path: 'article/edit', name: 'ArticleCreate', component: () => import('@/views/admin/ArticleEdit.vue'), meta: { title: '新建文章' } },
      { path: 'article/edit/:id', name: 'ArticleUpdate', component: () => import('@/views/admin/ArticleEdit.vue'), meta: { title: '编辑文章' } },
      { path: 'travel-templates', name: 'TravelTemplateManage', component: () => import('@/views/admin/TravelTemplateManage.vue'), meta: { title: '游记模板' } },
      { path: 'article/template/new', name: 'ArticleTemplateCreate', component: () => import('@/views/admin/ArticleTemplateEdit.vue'), meta: { title: '新建模板文章' } },
      { path: 'article/template/edit/:id', name: 'ArticleTemplateUpdate', component: () => import('@/views/admin/ArticleTemplateEdit.vue'), meta: { title: '编辑模板文章' } },
      { path: 'categories', name: 'CategoryManage', component: () => import('@/views/admin/CategoryManage.vue'), meta: { title: '分类管理' } },
      { path: 'tags', name: 'TagManage', component: () => import('@/views/admin/TagManage.vue'), meta: { title: '标签管理' } },
      { path: 'comments', name: 'CommentManage', component: () => import('@/views/admin/CommentManage.vue'), meta: { title: '评论管理' } },
      { path: 'friend-links', name: 'FriendLinkManage', component: () => import('@/views/admin/FriendLinkManage.vue'), meta: { title: '友链管理' } },
      { path: 'visits', name: 'VisitManage', component: () => import('@/views/admin/VisitManage.vue'), meta: { title: '访问统计' } },
      { path: 'users', name: 'UserManage', component: () => import('@/views/admin/UserManage.vue'), meta: { title: '用户管理' } },
      { path: 'profile', name: 'Profile', component: () => import('@/views/admin/Profile.vue'), meta: { title: '个人资料' } }
    ]
  },
  // 前台
  {
    path: '/',
    component: () => import('@/layouts/FrontLayout.vue'),
    children: [
      { path: '', name: 'Home', component: () => import('@/views/front/Home.vue'), meta: { title: '首页' } },
      { path: 'articles', name: 'ArticleList', component: () => import('@/views/front/ArticleList.vue'), meta: { title: '文章列表' } },
      { path: 'article/:id', name: 'ArticleDetail', component: () => import('@/views/front/ArticleDetail.vue'), meta: { title: '文章详情', fullWidth: true } },
      { path: 'categories', name: 'CategoryArchive', component: () => import('@/views/front/CategoryArchive.vue'), meta: { title: '分类归档' } },
      { path: 'about', name: 'About', component: () => import('@/views/front/About.vue'), meta: { title: '关于' } },
      { path: 'search', name: 'Search', component: () => import('@/views/front/Search.vue'), meta: { title: '搜索' } },
      { path: 'write', name: 'UserArticleCreate', component: () => import('@/views/front/UserArticleEdit.vue'), meta: { title: '写文章' } },
      { path: 'my/articles', name: 'MyArticles', component: () => import('@/views/front/MyArticles.vue'), meta: { title: '我的文章' } },
      { path: 'my/articles/edit/:id', name: 'UserArticleEdit', component: () => import('@/views/front/UserArticleEdit.vue'), meta: { title: '编辑文章' } }
    ]
  },
  // 独立页面
  {
    path: '/other/nbsz',
    name: 'Nbsz',
    component: () => import('@/views/front/other/nbsz.vue'),
    meta: { title: '尚铸新材料', standalone: true }
  },
  // 404
  { path: '*', redirect: '/' }
]

const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 }
  }
})

router.beforeEach(async (to, from, next) => {
  NProgress.start()
  document.title = to.meta.title ? `${to.meta.title} - wuBlog` : 'wuBlog'

  const token = getToken()

  // 检查是否访问后台（除了登录页）
  if (to.path.startsWith('/admin') && to.path !== '/admin/login') {
    if (!token) {
      next('/admin/login')
      NProgress.done()
      return
    }
    try {
      const store = router.app.$store
      if (!store.getters['user/isLoggedIn'] || !store.getters['user/userInfo']) {
        await store.dispatch('user/getUserInfo')
      }
      const role = store.getters['user/role']
      if (role !== 1) {
        next('/')
        NProgress.done()
        return
      }
    } catch (e) {
      next('/admin/login')
      NProgress.done()
      return
    }
  }

  if (to.meta.requiresAuth) {
    if (!token) {
      next('/admin/login')
      NProgress.done()
      return
    }
    try {
      const store = router.app.$store
      if (!store.getters['user/isLoggedIn'] || !store.getters['user/userInfo']) {
        await store.dispatch('user/getUserInfo')
      }

      // 检查管理员权限
      if (to.meta.requiresAdmin) {
        const role = store.getters['user/role']
        if (role !== 1) {
          next('/')
          NProgress.done()
          return
        }
      }
    } catch (e) {
      next('/admin/login')
      NProgress.done()
      return
    }
  }

  if (to.path === '/admin/login' && token) {
    const store = router.app.$store
    const role = store.getters['user/role']
    if (role === 1) {
      next('/admin/dashboard')
      NProgress.done()
      return
    } else {
      next('/')
      NProgress.done()
      return
    }
  }

  next()
})

router.afterEach((to) => {
  NProgress.done()

  if (!to.path.startsWith('/admin')) {
    const articleId = to.params.id ? Number(to.params.id) : null
    recordVisit({ articleId, pagePath: to.path }).catch(() => {})
  }
})

export default router
